import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';

const Home: React.FC = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    // const [isHovered, setIsHovered] = useState<string | null>(null);

    // Set page title
    usePageTitle('Home');

    // Hero image URL from Unsplash (beautiful golf course)
    const heroImageUrl = "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?auto=format&fit=crop&q=80";

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.colors.BACKGROUND,
            color: theme.colors.TEXT,
        }}>
            {/* Hero Section */}
            <div style={{
                position: 'relative',
                height: '70vh',
                minHeight: '500px',
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${heroImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding: `${theme.spacing.XLARGE}px`,
            }}>
                <h1 style={{
                    color: theme.colors.WHITE,
                    fontSize: '3.5rem',
                    marginBottom: theme.spacing.XLARGE,
                    fontWeight: 'bold',
                }}>
                    FG2P
                </h1>
                <p style={{
                    color: theme.colors.WHITE,
                    fontSize: '1.5rem',
                    maxWidth: '800px',
                    marginBottom: theme.spacing.XLARGE * 2,
                    lineHeight: 1.8,
                    letterSpacing: '0.02em',
                }}>
                    Fairway, Green, 2 Putt - Your path to better golf.
                </p>
                <p style={{
                    color: theme.colors.WHITE,
                    fontSize: '1.25rem',
                    maxWidth: '800px',
                    marginBottom: theme.spacing.XLARGE * 2,
                    lineHeight: 1.8,
                    letterSpacing: '0.02em',
                    opacity: 0.9,
                }}>
                    Track your rounds, analyze your game, and improve your scores with our intelligent golf companion.
                </p>
                {user ? (
                    <Link to="/dashboard" style={{
                        backgroundColor: theme.colors.PRIMARY,
                        color: theme.colors.WHITE,
                        padding: `${theme.spacing.MEDIUM}px ${theme.spacing.XLARGE}px`,
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        transition: 'transform 0.2s ease',
                    }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        Let&apos;s Go
                    </Link>
                ) : (
                    <div style={{
                        display: 'flex',
                        gap: theme.spacing.LARGE,
                        marginTop: theme.spacing.LARGE,
                    }}>
                        <Link to="/register" style={{
                            backgroundColor: theme.colors.PRIMARY,
                            color: theme.colors.WHITE,
                            padding: `${theme.spacing.MEDIUM}px ${theme.spacing.LARGE}px`,
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            transition: 'transform 0.2s ease',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            Get Started
                        </Link>
                        <Link to="/login" style={{
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            color: theme.colors.WHITE,
                            padding: `${theme.spacing.MEDIUM}px ${theme.spacing.LARGE}px`,
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            backdropFilter: 'blur(5px)',
                            transition: 'transform 0.2s ease',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            Sign In
                        </Link>
                    </div>
                )}
            </div>

            {/* Content Section */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: `${theme.spacing.XLARGE}px ${theme.spacing.LARGE}px`,
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: theme.spacing.XLARGE * 1.5,
                    marginBottom: theme.spacing.XLARGE * 2,
                }}>
                    {/* Fairway Section */}
                    <div style={{
                        padding: theme.spacing.LARGE,
                        backgroundColor: theme.colors.SURFACE,
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}>
                        <h2 style={{
                            color: theme.colors.PRIMARY,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.5rem',
                        }}>Fairway</h2>
                        <p style={{
                            color: theme.colors.TEXT,
                            lineHeight: 1.8,
                            letterSpacing: '0.02em',
                            fontSize: '1.1rem',
                        }}>
                            Track your tee shots and approach accuracy. Get insights into your driving consistency and learn which clubs work best for different situations.
                        </p>
                    </div>

                    {/* Green Section */}
                    <div style={{
                        padding: theme.spacing.LARGE,
                        backgroundColor: theme.colors.SURFACE,
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}>
                        <h2 style={{
                            color: theme.colors.PRIMARY,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.5rem',
                        }}>Green</h2>
                        <p style={{
                            color: theme.colors.TEXT,
                            lineHeight: 1.6,
                        }}>
                            Analyze your approach shots and green-in-regulation statistics. Understand your strengths and areas for improvement in your short game.
                        </p>
                    </div>

                    {/* 2 Putt Section */}
                    <div style={{
                        padding: theme.spacing.LARGE,
                        backgroundColor: theme.colors.SURFACE,
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}>
                        <h2 style={{
                            color: theme.colors.PRIMARY,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.5rem',
                        }}>2 Putt</h2>
                        <p style={{
                            color: theme.colors.TEXT,
                            lineHeight: 1.6,
                        }}>
                            Master your putting game with detailed statistics on your performance on the greens. Track your putts per round and identify patterns in your putting game.
                        </p>
                    </div>
                </div>

                {!user && (
                    <div style={{
                        textAlign: 'center',
                        marginTop: theme.spacing.XLARGE,
                    }}>
                        <h2 style={{
                            color: theme.colors.TEXT,
                            marginBottom: theme.spacing.LARGE,
                            fontSize: '2rem',
                        }}>Ready to improve your game?</h2>
                        <Link to="/register" style={{
                            display: 'inline-block',
                            backgroundColor: theme.colors.PRIMARY,
                            color: theme.colors.WHITE,
                            padding: `${theme.spacing.MEDIUM}px ${theme.spacing.LARGE}px`,
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            transition: 'transform 0.2s ease',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            Start Your Journey
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Home; 